import {memo, useMemo} from 'react';
import styled, {css} from 'styled-components';
import {Microcopy} from '../../atoms/typography/microcopy';
import {TextTag2} from '../../atoms/tag';
import {IGroupMembers} from '../../../hooks';
import {CancelIcon} from '../../atoms/icons';
import {Tooltip} from '../tooltip';
import {ifProp} from 'styled-tools';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  position: relative;
`;

const Text = styled(Microcopy)`
  margin-left: 0px;
  color: #585adf;
  font-weight: 500;
`;

const Container = styled.div<{disabled?: boolean}>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  position: relative;
  .groupName .tooltip {
    visibility: hidden;
    width: auto;
    background-color: rgba(0, 0, 0, 0.7);
    font-size: 12px;
    color: #fff;
    text-align: center;
    padding: 12px;
    border-radius: 6px;
    position: absolute;
    opacity: 0;
    transition: opacity 0.3s;
    z-index: 1;
    margin-top: -90px;
  }
  .groupName:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }

  .groupName .tooltip::after {
    content: ' ';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: black transparent transparent transparent;
  }
  .groupName .cancel {
    svg {
      transform: scale(0.95);
      fill: #5f5f8c;
      stroke: #5f5f8c;
      ${ifProp(
        'disabled',
        css`
          fill: #bfbfd4;
        `,
      )};
      path {
        ${ifProp(
          'disabled',
          css`
            stroke: #bfbfd4;
          `,
        )};
      }
    }
  }
`;

export interface GroupDisplayItem {
  id: string;
  name: string;
  disabled?: boolean;
  value?: string | number;
}

type DisplayableGroup = GroupDisplayItem | IGroupMembers;

const getGroupId = (group: DisplayableGroup) => String(group.id);

const getGroupName = (group: DisplayableGroup) => group.name;

const getGroupDisabled = (group: DisplayableGroup) =>
  'disabled' in group ? group.disabled : undefined;

const getCancelValue = (group: DisplayableGroup) =>
  'value' in group && group.value !== undefined
    ? String(group.value)
    : String(group.id);

export interface GroupsProps {
  items: DisplayableGroup[];
  total: number;
  groupDisplayLength?: any;
  showCancelIcon?: boolean;
  textStyle?: {
    color: string;
    size: string;
    weight: number;
  };
  cancelFx?: (value: string, clearAll?: boolean) => void;
  disabled?: boolean;
}

export const Groups = memo(function ({
  total,
  items,
  showCancelIcon,
  textStyle,
  groupDisplayLength,
  cancelFx,
  disabled,
}: GroupsProps) {
  const renderMore = useMemo(() => {
    if (groupDisplayLength !== undefined) {
      if (total > groupDisplayLength) {
        return (
          <Tooltip
            BodyTextNodeType={Text}
            text={`+${total - groupDisplayLength}`}
            bodyStyle={{color: 'rgb(88, 90, 223)', fontWeight: 500}}
            tooltipBody={
              <span>
                {items.map((group, index) => (
                  <span key={getGroupId(group)}>
                    {`${getGroupName(group)}${
                      index === items?.length - 1 ? '' : ', '
                    }`}
                  </span>
                ))}
              </span>
            }
          />
        );
      }
    } else {
      if (total > 2) {
        return (
          <Tooltip
            BodyTextNodeType={Text}
            text={`+${total - 2}`}
            bodyStyle={{color: 'rgb(88, 90, 223)', fontWeight: 500}}
            tooltipBody={
              <span>
                {items.map((group, index) => (
                  <span key={getGroupId(group)}>
                    {`${getGroupName(group)}${
                      index === items?.length - 1 ? '' : ', '
                    }`}
                  </span>
                ))}
              </span>
            }
          />
        );
      }
    }

    return null;
  }, [items, total, groupDisplayLength]);

  const item = items.slice(0, groupDisplayLength ? groupDisplayLength : 2);

  return (
    <Wrapper>
      <Container disabled={disabled}>
        {item.map((group, idx) =>
          getGroupName(group) ? (
            <div className="groupName" key={`${idx}-${getGroupName(group)}`}>
              <TextTag2
                style={{
                  marginRight: '4px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  fontSize: textStyle?.size || undefined,
                  fontWeight: textStyle?.weight || undefined,
                  color:
                    disabled || getGroupDisabled(group)
                      ? '#bfbfd4'
                      : textStyle?.color,
                }}>
                {getGroupName(group)?.length > 12
                  ? getGroupName(group).substr(0, 12) + ' ...'
                  : getGroupName(group) || ''}
                {showCancelIcon && !getGroupDisabled(group) && (
                  <span
                    onClick={() => {
                      if (cancelFx) {
                        cancelFx(getCancelValue(group));
                      }
                    }}
                    className="cancel"
                    style={{marginLeft: 7}}>
                    {' '}
                    <CancelIcon />
                  </span>
                )}
              </TextTag2>
              {getGroupName(group) && getGroupName(group)?.length > 12 && (
                <span className="tooltip">{getGroupName(group)}</span>
              )}
            </div>
          ) : null,
        )}
        {renderMore}
      </Container>
    </Wrapper>
  );
});
export const Groups2 = memo(function ({
  total,
  items,
  showCancelIcon,
  cancelFx,
  disabled,
}: GroupsProps) {
  const renderMore = useMemo(() => {
    if (total > 2) {
      return (
        <Tooltip
          BodyTextNodeType={Text}
          text={`+${total - 2}`}
          bodyStyle={{color: 'rgb(88, 90, 223)', fontWeight: 500}}
          tooltipBody={
            <span>
              {items.map((group, index) => (
                <span key={getGroupId(group)}>
                  {`${getGroupName(group)}${
                    index === items?.length - 1 ? '' : ', '
                  }`}
                </span>
              ))}
            </span>
          }
        />
      );
    }

    return null;
  }, [items, total]);
  const item = items.slice(0, 2);

  return (
    <Wrapper>
      <Container disabled={disabled}>
        {item.map((group, idx) => (
          <div className="groupName" key={`${idx}-${getGroupName(group)}`}>
            <TextTag2
              style={{
                marginRight: '4px',
                borderRadius: '8px',
                display: 'flex',
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '150%',
                color: disabled ? '#bfbfd4' : '#1E1E2F',
              }}>
              {getGroupName(group)?.length > 12
                ? getGroupName(group).substr(0, 12) + ' ...'
                : getGroupName(group)}
              {showCancelIcon && (
                <span
                  onClick={() => {
                    if (cancelFx) {
                      cancelFx(getCancelValue(group));
                    }
                  }}
                  className="cancel"
                  style={{marginLeft: 7, color: '#5f5f8c'}}>
                  <CancelIcon />
                </span>
              )}
            </TextTag2>
            {getGroupName(group) && getGroupName(group)?.length > 12 && (
              <span className="tooltip">{getGroupName(group)}</span>
            )}
          </div>
        ))}
        {renderMore}
      </Container>
    </Wrapper>
  );
});
